import { useMemo, useState } from 'react';
import { Card, ElementContainer, Icon, Select, TextField, withStyles } from 'vopay';

import { useTranslation } from 'react-i18next';
import Dialog from '#shared/components/Dialog/Dialog';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import useLabels from '#shared/components/Contacts/hooks/useLabels';
import styles from './AddLabelDialog.module.scss';

import { Button } from '#shared/components/UI';

const cn = withStyles(styles);

type AddLabelDialogProps = {
  accountID: string;
  customerLabels: string[];
  clientAccountsEnabled: boolean;
  onClose: () => void;
  onAdd: (added: string) => void;
};

export default function AddLabelDialog({
  accountID,
  customerLabels,
  clientAccountsEnabled,
  onClose,
  onAdd,
}: AddLabelDialogProps) {
  // No accountID: the picker lists the account-wide labels.
  const { fetchingLabels, submitting, labels, addLabel } = useLabels({ clientAccountsEnabled });
  const alertDialog = useAlertDialog();
  const [label, setLabel] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.Overview.AddLabelDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLabels = useMemo(
    () =>
      labels
        .filter((label) => label.toLowerCase().includes(searchTerm.toLowerCase()) && !customerLabels.includes(label))
        .slice(0, 100)
        .map((label) => ({ label, value: label })),
    [labels, searchTerm, customerLabels],
  );

  async function handleAddLabel() {
    if (!label) return;

    try {
      await addLabel(label, accountID);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('unknownError', { defaultValue: 'Unknown error' });

      alertDialog.error(t('addLabelFailed', { defaultValue: `Failed to add ${label} Label`, label }), msg, {
        abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
      });

      return;
    }

    onAdd(label);
  }

  return (
    <Card className={cn('addLabelDialog')}>
      <Dialog.Title level="4" style={{ textAlign: 'center' }}>
        {t('title', { defaultValue: 'Add New Label' })}
      </Dialog.Title>

      <ElementContainer
        label={
          addingNew ? t('addLabel', { defaultValue: 'Add Label' }) : t('selectLabel', { defaultValue: 'Select Label' })
        }
        padding="0px 0px 8px"
        isBold={false}
        input={
          <Select
            version="2"
            name="addLabelSelect"
            emptyFilterMsg={t('noLabelsFound', { defaultValue: 'No Labels Found' })}
            filterable
            options={filteredLabels}
            renderSelectedValue={(data, { Placeholder }) =>
              addingNew ? t('newLabel', { defaultValue: 'New Label' }) : data ? data.value : <Placeholder />
            }
            renderDropdownFooter={({ close }) => (
              <Button
                iconLeft="add"
                style={{ width: '100%' }}
                onClick={() => {
                  setAddingNew(true);
                  close();
                }}
              >
                {t('newLabel', { defaultValue: 'New Label' })}
              </Button>
            )}
            onChange={(e) => {
              setLabel(e.target.value);
              setAddingNew(false);
            }}
            onFilter={(e) => {
              e.preventDefault();

              e.debounce(() => {
                setSearchTerm(e.filter);
              }, 300);
            }}
            disabled={fetchingLabels || submitting}
            loading={fetchingLabels}
          />
        }
      />

      {addingNew && (
        <TextField
          name="addNewLabel"
          variant="outlineShadow"
          disabled={fetchingLabels || submitting}
          placeholder={t('label', { defaultValue: 'Label' })}
          onChange={(e) => setLabel(e.target.value)}
          onRefChange={(input) => input.current?.focus()}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleAddLabel();
            }
          }}
        />
      )}

      <div className={cn('addLabelFooter')}>
        <Button onClick={onClose}>{tGlobal('Buttons.back', { defaultValue: 'Back' })}</Button>

        <Button colorScheme="primary" disabled={!label || submitting} onClick={handleAddLabel}>
          {submitting ? (
            <Icon icon="loader" height="40px" width="40px" />
          ) : (
            t('addLabel', { defaultValue: 'Add Label' })
          )}
        </Button>
      </div>
    </Card>
  );
}
